// src/components/ScheduleOrganizeCard.js

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card,
    CardContent,
    CardActions,
    IconButton,
    Typography,
    Box,
    useTheme,
    Avatar,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Import the images
import calendarImageLight from './images/calendar-image-light.png'; // Adjust the path if needed
import calendarImageDark from './images/calendar-image-dark.png';   // Adjust the path if needed

const ScheduleOrganizeCard = () => {
    const theme = useTheme();

    // Card styling
    const cardBackgroundColor = theme.palette.mode === 'dark' ? '#30334E' : '#FFFFFF';

    // Select the appropriate image based on the theme mode
    const calendarImage = theme.palette.mode === 'dark' ? calendarImageDark : calendarImageLight;

    return (
        <Card
            elevation={4}
            sx={{
                position: 'relative',
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: cardBackgroundColor,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15)',
                },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flexGrow: 1,
                width: '100%',
                height: '100%',
            }}
        >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                        sx={{
                            bgcolor: 'transparent',
                            mr: 2,
                            width: 56,
                            height: 56,
                        }}
                    >
                        <CalendarTodayIcon sx={{ color: theme.palette.text.primary, fontSize: 32 }} />
                    </Avatar>
                    <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                        Schedule & Organize
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Plan your content ahead of time with our intuitive calendar.
                </Typography>
            </CardContent>

            {/* Calendar Illustration */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    pointerEvents: 'none', 
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        bottom: -1,
                        left: 0,
                        width: '100%',
                        height: '100px',
                        background: `linear-gradient(to bottom, rgba(255,255,255,0) 0%, ${theme.palette.background.default
                            } 100%)`,
                        zIndex: 2,
                    },
                }}
            >
                <Box
                    component="img"
                    className="calendar-image"
                    src={calendarImage}
                    alt="Calendar Illustration"
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: { xs: '200px', sm: '250px', md: '300px' }, // Responsive sizing
                        height: 'auto',
                        transform: 'translate(25%, 25%)', // Moves the image partly out of the card
                        transition: 'transform 0.3s ease',
                    }}
                />
            </Box>


            <CardActions sx={{ justifyContent: 'space-between', pt: "48px", position: 'relative', zIndex: 2 }}>
                <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                        No upcoming events
                    </Typography>
                </Box>
                <IconButton
                    component={RouterLink}
                    to="/schedule"
                    sx={{ color: theme.palette.primary.main }}
                >
                    <ArrowForwardIcon />
                </IconButton>
            </CardActions>
        </Card>
    );
};

export default ScheduleOrganizeCard;
